import React, { FC, useEffect, useMemo, useState } from 'react'
import print from 'styles/components/print.module.scss'
import text from 'styles/utility/text.module.scss'
import format from 'date-fns/format'
import { MainHeadline } from 'components/Typography'
import { PrintDeliveryOrderCard } from 'components/Card'
import { countOrderTimes, fetchOrders, initOrders, unsubscribeOrders } from 'reducks/checkout/operations'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getStoreRef } from 'reducks/auth/selectors'
import { getOrders, getOrderTimes } from 'reducks/checkout/selectors'
import { Spacer } from 'components/Utility'
import HTMLReactParser from 'html-react-parser'
import { FlexRow } from 'components/Flex'
import { getBrowserName } from 'lib/judge'
import { CHROME_SHEET_HEIGHT, ORDER_HEADER_FOOTER, SAFARI_SHEET_HEIGHT } from 'constants/print'
import { StockUnionType } from 'types/stock'
import { fetchStoreShippings, initStoreShippings, unsubscribeStoreShippings } from 'reducks/storeShipping/operations'
import { StoreShippingType } from 'types/storeShipping'
import { getStoreShippings } from 'reducks/storeShipping/selectors'
import { fetchUsers } from 'reducks/user/operations'
import { OrderTime } from 'types/checkout'
import { getUsers } from 'reducks/user/selectors'
import { fetchBreads } from 'reducks/bread/operations'
import { getBreads } from 'reducks/bread/selectors'

type Props = {
  currentDate: Date
  date: string | undefined
  status: string
  title: string
  types: StockUnionType[]
}

const PrintDeliveryOrderTemplate: FC<Props> = (props) => {
  const { currentDate, date, status, title, types } = props
  const dispatch = useDispatch()
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const storeShippings: StoreShippingType[] = useSelector((state: State) => getStoreShippings(state))
  const breads = useSelector((state: State) => getBreads(state))
  const orders = useSelector((state: State) => getOrders(state))
  const orderTimes: OrderTime[] = useSelector((state: State) => getOrderTimes(state))
  const users = useSelector((state: State) => getUsers(state))
  const storeRefs = useMemo(() => [storeRef], [storeRef])

  const [printedCards, setPrintedCards] = useState<JSX.Element[]>([])
  const [isReadyPrint, setReadyPrint] = useState<boolean>(false)
  const isChrome = getBrowserName() === 'chrome'

  // 受取番号に対応するユーザー情報を取得する
  const userIds = useMemo(() => storeShippings.map((basket) => basket.user_id), [storeShippings])

  useEffect(() => {
    if (orders.length === 0 || storeShippings.length === 0 || orderTimes.length === 0 || breads.length === 0) return
    // レンダリングを待つために1000ミリ秒のwait処理
    setTimeout(() => {
      const documents = Array.from(document.getElementsByClassName('card-parent'))
      if (documents.length === 0) return
      const cards: JSX.Element[] = []
      const SHEET_HEIGHT = isChrome
        ? CHROME_SHEET_HEIGHT - ORDER_HEADER_FOOTER
        : SAFARI_SHEET_HEIGHT - ORDER_HEADER_FOOTER
      let currentHeight = 0
      // 改ページが必要なカードのindexを取得
      const breakBeforePages: number[] = [0]
      for (let i = 0; i < documents.length; i = (i + 1) | 0) {
        const document = documents[i]
        if (!document) continue
        currentHeight += document.clientHeight
        if (currentHeight >= SHEET_HEIGHT) {
          breakBeforePages.push(i)
          currentHeight = document.clientHeight
        }
      }
      for (let i = 0; i < breakBeforePages.length; i = (i + 1) | 0) {
        const startRefIndex = breakBeforePages[i]
        // 最終ページかどうかを判定
        const slicedDocuments =
          i + 1 === breakBeforePages.length
            ? documents.slice(startRefIndex)
            : documents.slice(startRefIndex, breakBeforePages[i + 1])
        const maxLength = slicedDocuments.reduce((prev, document) => {
          const length = document.childNodes[0]?.childNodes[0]?.firstChild?.textContent?.length
          if (!length) return prev
          if (prev > length) return prev
          return length
        }, 0)
        cards.push(
          <section className={isChrome ? print.sheet_chrome : print.sheet_safari}>
            {isChrome ? (
              <Spacer size={49} /> /* Chrome用ヘッダの余白 */
            ) : (
              <>
                <Spacer size={12} />
                <FlexRow justifyContent={'between'}>
                  <MainHeadline headline={title} />
                  <p className={`${text.font_small} ${text.align_right}`}>
                    {format(new Date(), 'yyyy年M月d日HH:mm時点')}
                  </p>
                </FlexRow>
                <Spacer size={12} />
              </>
            )}
            {slicedDocuments.map((document) => {
              if (!document) return
              return HTMLReactParser(document.innerHTML)
            })}
            <div className={print.footer}>
              ページ {i + 1}/{breakBeforePages.length}
            </div>
          </section>
        )
      }
      setPrintedCards([...cards])
      setReadyPrint(true)
    }, 1000)
  }, [orders, orderTimes, storeShippings, breads])

  useEffect(() => {
    if (!isReadyPrint) return
    setTimeout(() => {
      if (isChrome) {
        window.print()
      } else {
        document.execCommand('print', false, undefined)
      }
    }, 500)
  }, [isReadyPrint])

  // 日付が変わったら受取番号を取得する
  useEffect(() => {
    if (!date || !storeRef) return
    dispatch(initStoreShippings())
    dispatch(fetchStoreShippings(date, storeRef))
    return () => {
      dispatch(unsubscribeStoreShippings())
    }
  }, [date, storeRef])

  // ステータスまたは注文タイプが変更されたら注文情報を再取得する
  useEffect(() => {
    if (!date || !storeRef || !status || !types) return
    dispatch(initOrders())
    dispatch(
      fetchOrders(
        date,
        status,
        types,
        status === 'すべて' ? ['succeeded', 'requires_capture', 'canceled'] : ['succeeded', 'requires_capture'],
        storeRef
      )
    )
    return () => {
      dispatch(unsubscribeOrders())
    }
  }, [date, storeRef, status, types])

  // 注文回数を取得
  useEffect(() => {
    if (
      !currentDate ||
      !storeRefs ||
      !userIds ||
      storeRefs.length === 0 ||
      userIds.length === 0 ||
      orderTimes.length > 0
    )
      return
    dispatch(countOrderTimes(userIds, currentDate, storeRefs))
  }, [userIds, currentDate, storeRefs])

  // 顧客情報を取得
  useEffect(() => {
    if (!userIds || userIds.length === 0 || users.length > 0) return
    dispatch(fetchUsers(userIds))
  }, [userIds])

  // パン情報を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBreads(storeRef, 'all', 'name'))
  }, [storeRef])

  return (
    <div>
      <section className={print.screenArea}>
        <Spacer size={12} />
        <FlexRow justifyContent={'between'}>
          <MainHeadline headline={title} />
          <p className={`${text.font_small} ${text.align_right}`}>{format(new Date(), 'yyyy年M月d日HH:mm時点')}</p>
        </FlexRow>
        <Spacer size={12} />
        {storeShippings.length > 0 &&
          orderTimes.length > 0 &&
          orders.length > 0 &&
          orders
            .sort((a, b) => {
              return a.checkout_at.toDate().getTime() < b.checkout_at.toDate().getTime() ? -1 : 1
            })
            .map((order) => {
              const user = users.find((_user) => _user.user_id === order.user_id)
              if (!user) return null
              const orderTime = orderTimes.find((_orderTime) => _orderTime.user_id === order.user_id)
              if (!orderTime) return null
              const storeShipping = storeShippings.find((_) => _.store_checkout_ref.id === order.checkout_id)
              if (!storeShipping) return null
              return (
                <div className={'card-parent'} key={order.checkout_id}>
                  <PrintDeliveryOrderCard
                    deliveryAddress={storeShipping.delivery_address}
                    isGift={storeShipping.is_gift}
                    order={order}
                    orderTimes={orderTime.times}
                    scheduledDeliveryDate={storeShipping.scheduled_delivery_date}
                    scheduledDeliveryTime={storeShipping.scheduled_delivery_time}
                    senderName={storeShipping.sender_name}
                    shippingNumber={storeShipping.shipping_number}
                    user={user}
                  />
                  <Spacer size={12} />
                </div>
              )
            })}
      </section>
      {/* 印刷用エリア(ブラウザでは非表示) */}
      <section className={print.printArea}>
        {isChrome && (
          <div className={print.header}>
            <MainHeadline headline={title} />
            <p className={`${text.font_small} ${text.align_right}`}>{format(new Date(), 'yyyy年M月d日HH:mm時点')}</p>
          </div>
        )}
        {printedCards}
      </section>
    </div>
  )
}

export default PrintDeliveryOrderTemplate
